/* @jsx h */
import { LemmonModal } from "@app/vue/components/index";
import { useNativeBridge } from "@app/vue/providers/NativeBridge";
import { useTrpc } from "@app/vue/providers/index";
import type {
  EpsonPrinterInfo,
  EpsonPrinterStatus,
} from "@app/vue/providers/bridge-rpc-types";
import { useLocalization } from "@lemmonhq/uikit";
import {
  defineComponent,
  ref,
  h,
  onUnmounted,
  computed,
  nextTick,
  watch,
} from "vue";
import { useToast } from "../../providers";
import {
  classifySetupError,
  detectEpsonSeries,
  normalizePasswordCheck,
  saveBluetoothPrinter,
  type SavedBluetoothPrinter,
  type SetupError,
  type SetupStage,
} from "./bluetoothPrinter";
import {
  CompleteStep,
  ConfiguringStep,
  DiscoverStep,
  PasswordStep,
  WelcomeStep,
  type ConfigureStage,
} from "./PrinterSetupSteps";
import {
  RegisterStep,
  type ExistingPrinter,
  type RegisterForm,
  type RegisterFormError,
} from "./PrinterRegisterStep";
import { WifiStep, type WifiPhase } from "./PrinterWifiStep";
import type { WifiScan } from "./PrinterWifiNetworkPicker";
import {
  applyWifiConfig,
  checkSavedWifi,
  isIpv4,
  printerWifiNetworks,
  readSerialNumber,
  validateWifiConfig,
  type Ipv4Config,
  type WifiCheck,
  type WifiConfig,
  type WifiFormError,
} from "./printerWifi";

type WizardStep =
  | "welcome"
  | "network"
  | "discover"
  | "configuring"
  | "password"
  | "wifi"
  | "complete"
  | "register";

// Epson printers take roughly 30s to restart after a settings change and
// refuse Bluetooth until they're back.
const RESTART_WAIT_MS = 20_000;
const RECONNECT_INTERVAL_MS = 5_000;
const RECONNECT_TIMEOUT_MS = 90_000;

// The modal body scrolls, not the page.
const scrollParent = (element: HTMLElement | null): HTMLElement | null => {
  for (let node = element?.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
  }
  return null;
};

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const errorDetail = (error: unknown) =>
  error instanceof Error ? error.message : String(error ?? "");

// App builds that can list nearby Wi-Fi networks (Android).
const canListWifiNetworks = () =>
  ((window as any).lemmonEnv?.app?.capabilities?.wifiNetworks ?? 0) >= 1;

// The network is chosen first, while the phone is still on the restaurant's
// Wi-Fi, and applied once the printer is reachable over Bluetooth.
const WIZARD_STEPS: WizardStep[] = [
  "welcome",
  "network",
  "discover",
  "configuring",
  "password",
  "wifi",
  "complete",
  "register",
];

// Secondary actions stack above the primary one, which sits full width at the
// bottom of the sheet.
const FOOTER = "flex flex-col gap-2 px-4 py-4";

type SecureSettings = { password: string; doVerification: boolean };

export const PrinterSetupWizard = defineComponent<{
  onClose: () => void;
  onSuccess?: (printer: SavedBluetoothPrinter) => void;
}>({
  name: "PrinterSetupWizard",
  props: ["onClose", "onSuccess"],
  setup(props) {
    const { t } = useLocalization();
    const toast = useToast();
    const bridge = useNativeBridge();
    const trpc = useTrpc();

    const currentStep = ref<WizardStep>("welcome");
    const foundPrinters = ref<EpsonPrinterInfo[]>([]);
    const selectedPrinter = ref<EpsonPrinterInfo | null>(null);
    const savedPrinter = ref<SavedBluetoothPrinter | null>(null);
    const printerStatus = ref<EpsonPrinterStatus | null>(null);
    const isSearching = ref(false);
    const isConfiguring = ref(false);
    const isPrinting = ref(false);
    const isVerifying = ref(false);
    const adminPassword = ref("");
    const searchAttempted = ref(false);
    const setupError = ref<SetupError | null>(null);
    const configureStage = ref<ConfigureStage>("connect");
    const wifiConfig = ref<WifiConfig>({
      ssid: "",
      security: "WPA/WPA2-PSK",
      passphrase: "",
      ipv4: { mode: "Auto", ip: "", subnetMask: "", gateway: "" },
    });
    // Set once the printer accepted new network settings.
    const appliedIpv4 = ref<Ipv4Config | null>(null);
    const serialNumber = ref("");
    const registerForm = ref<RegisterForm>({ name: "", ip: "" });
    const registerFormError = ref<RegisterFormError | null>(null);
    const duplicates = ref<ExistingPrinter[]>([]);
    const isCheckingSerial = ref(false);
    const isRegistering = ref(false);
    const wifiPhase = ref<WifiPhase>("form");
    const wifiCheck = ref<WifiCheck | null>(null);
    let isUnmounted = false;
    const wifiFormError = ref<WifiFormError | null>(null);
    // null when the app can't list networks; the SSID is typed instead.
    const wifiScan = ref<WifiScan | null>(null);
    const networkPrinters = ref<EpsonPrinterInfo[]>([]);
    const isSearchingNetwork = ref(false);
    const networkSearchAttempted = ref(false);

    const stepIndex = computed(() => WIZARD_STEPS.indexOf(currentStep.value));
    const content = ref<HTMLElement | null>(null);

    // Each step starts at the top, not where the last one was scrolled to.
    watch(currentStep, async () => {
      await nextTick();
      scrollParent(content.value)?.scrollTo({ top: 0 });
    });

    const fail = (stage: SetupStage, error: unknown) => {
      console.error(`Bluetooth printer setup failed at ${stage}:`, error);
      setupError.value = classifySetupError(stage, error);
    };

    const stopDiscovery = async () => {
      if (!bridge) return;
      try {
        await bridge.rpc.request("epsonStopDiscovery");
      } catch {
        // Discovery may already have stopped
      }
    };

    const disconnectPrinter = async () => {
      if (!bridge) return;
      try {
        if (await bridge.rpc.request("epsonIsConnected")) {
          await bridge.rpc.request("epsonDisconnect");
        }
      } catch {
        // Nothing to clean up if the link is already gone
      }
    };

    const startDiscovery = async () => {
      setupError.value = null;
      searchAttempted.value = true;
      selectedPrinter.value = null;
      foundPrinters.value = [];
      currentStep.value = "discover";

      if (!bridge) {
        setupError.value = { kind: "no_bridge", detail: "" };
        return;
      }

      isSearching.value = true;
      try {
        const permission = await bridge.rpc.request(
          "requestBluetoothPermissions",
        );
        if (!permission.granted) {
          throw new Error(
            `Bluetooth permission not granted: ${permission.status}`,
          );
        }
        const printers = await bridge.rpc.request("epsonDiscoverPrinters", {
          portType: "BLUETOOTH",
          timeout: 30,
        });
        foundPrinters.value = [...printers].sort((left, right) =>
          left.deviceName.localeCompare(right.deviceName),
        );
      } catch (error) {
        fail("discover", error);
      } finally {
        isSearching.value = false;
        await stopDiscovery();
      }
    };

    const connect = async (printer: EpsonPrinterInfo) => {
      await bridge!.rpc.request("epsonConnect", {
        target: printer.target,
        model: { series: detectEpsonSeries(printer.deviceName) },
      });
    };

    const handleSelectPrinter = async (printer: EpsonPrinterInfo) => {
      if (!bridge) {
        setupError.value = { kind: "no_bridge", detail: "" };
        return;
      }

      selectedPrinter.value = printer;
      setupError.value = null;
      printerStatus.value = null;
      currentStep.value = "configuring";
      isConfiguring.value = true;

      let stage: ConfigureStage = "connect";
      try {
        configureStage.value = stage;
        await connect(printer);

        stage = "status";
        configureStage.value = stage;
        printerStatus.value = await bridge.rpc.request("epsonGetPrinterStatus");

        adminPassword.value = "";
        currentStep.value = "password";
      } catch (error) {
        fail(stage, error);
      } finally {
        isConfiguring.value = false;
        await disconnectPrinter();
      }
    };

    const scanWifiNetworks = async () => {
      if (!bridge || !canListWifiNetworks()) return;
      wifiScan.value = { status: "scanning" };
      try {
        const networks = await bridge.rpc.request("getWifiNetworks");
        if (isUnmounted) return;
        wifiScan.value = {
          status: "done",
          networks: printerWifiNetworks(networks),
        };
      } catch (error) {
        console.warn("Wi-Fi scan failed:", error);
        if (isUnmounted) return;
        wifiScan.value = { status: "failed", detail: errorDetail(error) };
      }
    };

    const finishSetup = (password: string | null) => {
      const printer = selectedPrinter.value;
      if (!printer) return;
      setupError.value = null;
      savedPrinter.value = saveBluetoothPrinter(
        printer,
        detectEpsonSeries(printer.deviceName),
        password,
      );
      wifiFormError.value = null;
      currentStep.value = "wifi";
      void handleApplyWifi();
    };

    const startNetworkStep = () => {
      setupError.value = null;
      wifiFormError.value = null;
      currentStep.value = "network";
      if (!wifiScan.value || wifiScan.value.status === "failed") {
        void scanWifiNetworks();
      }
    };

    // Opens the pairing guide; the search starts once the printer is in
    // pairing mode and staff tap Search.
    const handleChooseNetwork = () => {
      wifiFormError.value = validateWifiConfig(wifiConfig.value);
      if (wifiFormError.value) return;
      setupError.value = null;
      searchAttempted.value = false;
      foundPrinters.value = [];
      currentStep.value = "discover";
    };

    const secureSettings = (): SecureSettings => ({
      password: savedPrinter.value?.adminPassword ?? "",
      doVerification: false,
    });

    const handleApplyWifi = async () => {
      const printer = selectedPrinter.value;
      if (!bridge || !printer) return;

      setupError.value = null;
      wifiFormError.value = validateWifiConfig(wifiConfig.value);
      if (wifiFormError.value) return;

      const secure = secureSettings();
      wifiPhase.value = "applying";
      try {
        await connect(printer);
        const settings = await bridge.rpc.request("epsonGetPrinterSettingEx", {
          secure,
        });
        serialNumber.value = readSerialNumber(settings) || serialNumber.value;
        // The printer restarts to join the network, which drops Bluetooth.
        await bridge.rpc.request("epsonSetPrinterSettingEx", {
          jsonSettings: applyWifiConfig(settings, wifiConfig.value),
          secure,
        });
      } catch (error) {
        wifiPhase.value = "form";
        fail("wifi", error);
        return;
      } finally {
        await disconnectPrinter();
      }

      appliedIpv4.value = { ...wifiConfig.value.ipv4 };
      networkPrinters.value = [];
      networkSearchAttempted.value = false;
      wifiCheck.value = null;
      wifiPhase.value = "verifying";
      const check = await readBackWifi(printer, secure);
      if (isUnmounted) return;
      wifiCheck.value = check;
      wifiPhase.value = "applied";
    };

    // Epson's manual asks to read the settings back after a change: some
    // printers report success without applying it.
    const readBackWifi = async (
      printer: EpsonPrinterInfo,
      secure: SecureSettings,
    ): Promise<WifiCheck> => {
      const deadline = Date.now() + RECONNECT_TIMEOUT_MS;
      let lastError: unknown = null;
      await sleep(RESTART_WAIT_MS);
      while (!isUnmounted) {
        try {
          await connect(printer);
          const settings = await bridge!.rpc.request(
            "epsonGetPrinterSettingEx",
            { secure },
          );
          return checkSavedWifi(settings, wifiConfig.value);
        } catch (error) {
          lastError = error;
        } finally {
          await disconnectPrinter();
        }
        if (Date.now() >= deadline) break;
        await sleep(RECONNECT_INTERVAL_MS);
      }
      return { status: "unreachable", detail: errorDetail(lastError) };
    };

    const startRegister = async () => {
      const printer = selectedPrinter.value;
      if (!printer) return;
      setupError.value = null;
      registerFormError.value = null;
      duplicates.value = [];
      registerForm.value = {
        name: printer.deviceName,
        ip: appliedIpv4.value?.mode === "Manual" ? appliedIpv4.value.ip : "",
      };
      currentStep.value = "register";

      // The serial was read while applying the Wi-Fi settings.
      if (!serialNumber.value) return;
      isCheckingSerial.value = true;
      try {
        duplicates.value = await trpc.printers.findBySerial.query({
          serial_number: serialNumber.value,
        });
      } catch (error) {
        // The warning is a courtesy; adding the printer still works without it.
        console.warn("Could not check for existing printers:", error);
      } finally {
        isCheckingSerial.value = false;
      }
    };

    const handleRegister = async () => {
      const form = registerForm.value;
      setupError.value = null;
      registerFormError.value = !form.name.trim()
        ? "name_required"
        : !isIpv4(form.ip)
          ? "ip_invalid"
          : null;
      if (registerFormError.value) return;

      isRegistering.value = true;
      try {
        await trpc.printers.create.mutate({
          name: form.name.trim(),
          ip: form.ip.trim(),
          serial_number: serialNumber.value,
        });
        handleFinish("devices.bt_printer.register_added");
      } catch (error) {
        fail("register", error);
      } finally {
        isRegistering.value = false;
      }
    };

    const handleRetryWifi = () => {
      setupError.value = null;
      wifiCheck.value = null;
      wifiPhase.value = "form";
    };

    const handleSearchNetwork = async () => {
      if (!bridge) return;
      setupError.value = null;
      isSearchingNetwork.value = true;
      try {
        networkPrinters.value = await bridge.rpc.request(
          "epsonDiscoverPrinters",
          { portType: "TCP", timeout: 15 },
        );
      } catch (error) {
        fail("network_search", error);
      } finally {
        networkSearchAttempted.value = true;
        isSearchingNetwork.value = false;
        await stopDiscovery();
      }
    };

    const handleVerifyPassword = async () => {
      const printer = selectedPrinter.value;
      if (!bridge || !printer) return;

      setupError.value = null;
      isVerifying.value = true;
      try {
        await connect(printer);
        const result = normalizePasswordCheck(
          await bridge.rpc.request("epsonVerifyPassword", {
            password: adminPassword.value,
          }),
        );
        if (result === "INVALID_PASSWORD") {
          setupError.value = { kind: "password_invalid", detail: "" };
        } else {
          // NO_PASSWORD / UNSUPPORTED: nothing to store for later settings calls.
          finishSetup(result === "OK" ? adminPassword.value || null : null);
        }
      } catch (error) {
        fail("password", error);
      } finally {
        isVerifying.value = false;
        await disconnectPrinter();
      }
    };

    const handleTestPrint = async () => {
      const printer = selectedPrinter.value;
      if (!bridge || !printer) return;

      setupError.value = null;
      isPrinting.value = true;
      try {
        await connect(printer);
        // The native module disconnects on its own once the job finishes.
        printerStatus.value = await bridge.rpc.request("epsonPrint", {
          commands: [
            {
              type: "text",
              data: `${t("devices.bt_printer.test_receipt_title")}\n`,
              align: "CENTER",
              size: { width: 2, height: 2 },
            },
            {
              type: "text",
              data: `\n${t("devices.bt_printer.test_receipt_body").replace(
                "{name}",
                printer.deviceName,
              )}\n${new Date().toLocaleString()}\n`,
              align: "CENTER",
            },
            { type: "feed", lines: 3 },
            { type: "cut" },
          ],
        });
        toast.showToast(t("devices.bt_printer.test_print_sent"), {
          type: "success",
          autoHide: 3000,
        });
      } catch (error) {
        fail("test_print", error);
        await disconnectPrinter();
      } finally {
        isPrinting.value = false;
      }
    };

    const handleFinish = (message = "devices.bt_printer.setup_complete") => {
      if (savedPrinter.value) props.onSuccess?.(savedPrinter.value);
      toast.showToast(t(message), {
        type: "success",
        autoHide: 3000,
      });
      props.onClose();
    };

    const handleBackToNetwork = () => {
      foundPrinters.value = [];
      selectedPrinter.value = null;
      setupError.value = null;
      searchAttempted.value = false;
      currentStep.value = "network";
    };

    const handleBackToDiscover = () => {
      setupError.value = null;
      configureStage.value = "connect";
      currentStep.value = "discover";
    };

    onUnmounted(async () => {
      isUnmounted = true;
      await stopDiscovery();
      await disconnectPrinter();
    });

    const renderProgressDots = () => (
      <div class="flex items-center justify-center gap-1.5 py-3">
        {WIZARD_STEPS.map((step, index) => (
          <div
            key={step}
            class={[
              "h-2 rounded-full transition-all duration-300",
              stepIndex.value === index
                ? "w-8 bg-primary"
                : stepIndex.value > index
                  ? "w-2 bg-primary/40"
                  : "w-2 bg-base-300",
            ]}
          />
        ))}
      </div>
    );

    const renderButtons = () => {
      if (currentStep.value === "welcome") {
        return (
          <div class={FOOTER}>
            <button onClick={props.onClose} class="btn btn-block btn-ghost">
              {t("general.words.cancel")}
            </button>
            <button
              onClick={startNetworkStep}
              disabled={!bridge}
              class="btn btn-block btn-primary"
            >
              <span class="material-symbols-outlined">arrow_forward</span>
              {t("devices.bt_printer.continue")}
            </button>
          </div>
        );
      }

      if (currentStep.value === "network") {
        return (
          <div class={FOOTER}>
            <button
              onClick={() => (currentStep.value = "welcome")}
              class="btn btn-block btn-ghost"
            >
              <span class="material-symbols-outlined">arrow_back</span>
              {t("general.words.back")}
            </button>
            <button
              onClick={handleChooseNetwork}
              class="btn btn-block btn-primary"
            >
              <span class="material-symbols-outlined">arrow_forward</span>
              {t("devices.bt_printer.continue")}
            </button>
          </div>
        );
      }

      if (currentStep.value === "discover") {
        return (
          <div class={FOOTER}>
            <button
              onClick={handleBackToNetwork}
              class="btn btn-block btn-ghost"
            >
              <span class="material-symbols-outlined">arrow_back</span>
              {t("general.words.back")}
            </button>
            <button
              onClick={startDiscovery}
              disabled={isSearching.value || !bridge}
              class="btn btn-block btn-primary"
            >
              {isSearching.value ? (
                <span class="loading loading-spinner loading-sm" />
              ) : (
                <span class="material-symbols-outlined">search</span>
              )}
              {isSearching.value
                ? t("devices.bt_printer.searching")
                : t("devices.bt_printer.search")}
            </button>
          </div>
        );
      }

      if (currentStep.value === "configuring") {
        return (
          <div class={FOOTER}>
            <button
              onClick={handleBackToDiscover}
              disabled={isConfiguring.value}
              class="btn btn-block btn-ghost"
            >
              <span class="material-symbols-outlined">arrow_back</span>
              {t("general.words.back")}
            </button>
            {setupError.value && selectedPrinter.value && (
              <button
                onClick={() => handleSelectPrinter(selectedPrinter.value!)}
                class="btn btn-block btn-primary"
              >
                <span class="material-symbols-outlined">refresh</span>
                {t("devices.bt_printer.try_again")}
              </button>
            )}
          </div>
        );
      }

      if (currentStep.value === "password") {
        return (
          <div class={FOOTER}>
            <button
              onClick={() => finishSetup(null)}
              disabled={isVerifying.value}
              class="btn btn-block btn-ghost"
            >
              {t("devices.bt_printer.skip")}
            </button>
            <button
              onClick={handleVerifyPassword}
              disabled={isVerifying.value}
              class="btn btn-block btn-primary"
            >
              {isVerifying.value ? (
                <span class="loading loading-spinner loading-sm" />
              ) : (
                <span class="material-symbols-outlined">key</span>
              )}
              {t("devices.bt_printer.verify")}
            </button>
          </div>
        );
      }

      if (currentStep.value === "wifi") {
        if (wifiPhase.value === "verifying") {
          return (
            <div class={FOOTER}>
              <button disabled class="btn btn-block btn-primary">
                <span class="loading loading-spinner loading-sm" />
                {t("devices.bt_printer.wifi_checking")}
              </button>
            </div>
          );
        }
        if (wifiPhase.value === "applied") {
          return (
            <div class={FOOTER}>
              {wifiCheck.value?.status !== "match" && (
                <button
                  onClick={handleRetryWifi}
                  disabled={isSearchingNetwork.value}
                  class="btn btn-block btn-ghost"
                >
                  <span class="material-symbols-outlined">refresh</span>
                  {t("devices.bt_printer.try_again")}
                </button>
              )}
              {wifiCheck.value?.status !== "mismatch" && (
                <button
                  onClick={() => (currentStep.value = "complete")}
                  disabled={isSearchingNetwork.value}
                  class="btn btn-block btn-primary"
                >
                  <span class="material-symbols-outlined">arrow_forward</span>
                  {t("devices.bt_printer.continue")}
                </button>
              )}
            </div>
          );
        }
        const isApplying = wifiPhase.value === "applying";
        return (
          <div class={FOOTER}>
            <button
              onClick={handleApplyWifi}
              disabled={isApplying}
              class="btn btn-block btn-primary"
            >
              {isApplying ? (
                <span class="loading loading-spinner loading-sm" />
              ) : (
                <span class="material-symbols-outlined">wifi</span>
              )}
              {t("devices.bt_printer.wifi_apply")}
            </button>
          </div>
        );
      }

      if (currentStep.value === "complete") {
        return (
          <div class={FOOTER}>
            <button
              onClick={startRegister}
              disabled={isPrinting.value}
              class="btn btn-block btn-primary"
            >
              <span class="material-symbols-outlined">arrow_forward</span>
              {t("devices.bt_printer.continue")}
            </button>
          </div>
        );
      }

      return (
        <div class={FOOTER}>
          <button
            onClick={() => handleFinish()}
            disabled={isRegistering.value}
            class="btn btn-block btn-ghost"
          >
            {t("devices.bt_printer.skip")}
          </button>
          <button
            onClick={handleRegister}
            disabled={isRegistering.value}
            class="btn btn-block btn-success"
          >
            {isRegistering.value ? (
              <span class="loading loading-spinner loading-sm" />
            ) : (
              <span class="material-symbols-outlined">done</span>
            )}
            {t("devices.bt_printer.register_submit")}
          </button>
        </div>
      );
    };

    return () => (
      <LemmonModal
        title={t("devices.bt_printer.title")}
        onClose={props.onClose}
        mobileVariant="full-screen"
        renderButtons={renderButtons}
      >
        <div ref={content} class="flex flex-col">
          {renderProgressDots()}

          {currentStep.value === "welcome" && (
            <WelcomeStep hasBridge={!!bridge} />
          )}
          {currentStep.value === "discover" && (
            <DiscoverStep
              isSearching={isSearching.value}
              searchAttempted={searchAttempted.value}
              printers={foundPrinters.value}
              error={setupError.value}
              onSelect={handleSelectPrinter}
            />
          )}
          {currentStep.value === "configuring" && (
            <ConfiguringStep
              printerName={selectedPrinter.value?.deviceName ?? ""}
              stage={configureStage.value}
              error={setupError.value}
            />
          )}
          {currentStep.value === "password" && (
            <PasswordStep
              printerName={selectedPrinter.value?.deviceName ?? ""}
              password={adminPassword.value}
              disabled={isVerifying.value}
              error={setupError.value}
              onInput={(value: string) => (adminPassword.value = value)}
              onSubmit={handleVerifyPassword}
            />
          )}
          {currentStep.value === "network" && (
            <WifiStep
              printerName=""
              phase="form"
              check={null}
              config={wifiConfig.value}
              formError={wifiFormError.value}
              wifiScan={wifiScan.value}
              error={null}
              isSearchingNetwork={false}
              networkSearchAttempted={false}
              networkPrinters={[]}
              onChange={(config: WifiConfig) => {
                wifiConfig.value = config;
                wifiFormError.value = null;
              }}
              onSubmit={handleChooseNetwork}
              onSearchNetwork={handleSearchNetwork}
              onRescanWifi={scanWifiNetworks}
            />
          )}
          {currentStep.value === "wifi" && (
            <WifiStep
              printerName={selectedPrinter.value?.deviceName ?? ""}
              phase={wifiPhase.value}
              check={wifiCheck.value}
              config={wifiConfig.value}
              formError={wifiFormError.value}
              wifiScan={wifiScan.value}
              error={setupError.value}
              isSearchingNetwork={isSearchingNetwork.value}
              networkSearchAttempted={networkSearchAttempted.value}
              networkPrinters={networkPrinters.value}
              onChange={(config: WifiConfig) => {
                wifiConfig.value = config;
                wifiFormError.value = null;
              }}
              onSubmit={handleApplyWifi}
              onSearchNetwork={handleSearchNetwork}
              onRescanWifi={scanWifiNetworks}
            />
          )}
          {currentStep.value === "complete" && (
            <CompleteStep
              printerName={selectedPrinter.value?.deviceName ?? ""}
              status={printerStatus.value}
              isPrinting={isPrinting.value}
              error={setupError.value}
              onTestPrint={handleTestPrint}
            />
          )}
          {currentStep.value === "register" && (
            <RegisterStep
              form={registerForm.value}
              formError={registerFormError.value}
              isIpAutofilled={appliedIpv4.value?.mode === "Manual"}
              serialNumber={serialNumber.value}
              isCheckingSerial={isCheckingSerial.value}
              duplicates={duplicates.value}
              disabled={isRegistering.value}
              error={setupError.value}
              onChange={(form: RegisterForm) => {
                registerForm.value = form;
                registerFormError.value = null;
              }}
              onSubmit={handleRegister}
            />
          )}
        </div>
      </LemmonModal>
    );
  },
});
